import type { Messages } from './messages';

/**
 * Copy for `src/app/tenant/microsoft/`. Owned by that page's author, and by nobody else.
 *
 * Every key starts with `tenantMicrosoft.` — the compiler refuses anything else, so
 * this file cannot collide with another page's. Shared words (save, cancel,
 * retry, statuses, role names) are already in `common.ts` and
 * `vocabulary.ts`; use those rather than restating them here.
 *
 * Microsoft's own screen names («App registrations», «Directory (tenant) ID» …)
 * stay in English on purpose: the operator is reading them off an English
 * Azure portal on the other screen, and a translation would be a label they
 * cannot find there.
 */
export const tenantMicrosoft = {
  'tenantMicrosoft.title': 'الدخول عبر Microsoft',
  'tenantMicrosoft.readOnly': 'رتبتك تسمح بعرض إعداد الدخول فقط. التعديل يحتاج صلاحية «تعديل المنشآت».',

  /* ---- Deployment ---------------------------------------------------- */
  'tenantMicrosoft.unavailable.title': 'الدخول عبر Microsoft غير مهيّأ على هذا النشر',
  'tenantMicrosoft.unavailable.body':
    'ينقص النشرَ أحدُ إعدادات البيئة (أحدها ENTRA_REDIRECT_URI، أو ENTRA_DEFAULT_RETURN_TO، أو ENTRA_SECRET_KEY)، فمسارات Microsoft تُجيب بأنها غير موجودة لكل منشأة أيًّا كان إعدادها. يمكن حفظ الإعداد هنا، لكنه لن يعمل قبل أن يُكمل فريق التشغيل إعداد النشر.',

  /* ---- Status -------------------------------------------------------- */
  'tenantMicrosoft.status.title': 'حالة الدخول',
  'tenantMicrosoft.status.description': 'كيف يدخل أعضاء المنشأة اليوم، وفق الإعداد المحفوظ لا وفق ما في النموذج.',
  'tenantMicrosoft.status.mode': 'طريقة الدخول',
  'tenantMicrosoft.mode.none': 'غير مهيّأ — كلمة المرور فقط',
  'tenantMicrosoft.mode.noneMeaning':
    'يدخل الأعضاء بالبريد وكلمة المرور. من يحاول الدخول بحساب Microsoft من دليل هذه المنشأة يُبلَّغ أن جهته غير مسجّلة.',
  'tenantMicrosoft.mode.both': 'Microsoft وكلمة المرور',
  'tenantMicrosoft.mode.bothMeaning': 'يدخل الأعضاء بحساب العمل في Microsoft أو بالبريد وكلمة المرور، أيّهما شاؤوا.',
  'tenantMicrosoft.mode.microsoftOnly': 'Microsoft فقط',
  'tenantMicrosoft.mode.microsoftOnlyMeaning':
    'الدخول بكلمة المرور مغلق. يدخل الأعضاء بحساب العمل في Microsoft وحده، ومن لا حساب له في الدليل لا يستطيع الدخول.',
  'tenantMicrosoft.mode.parked': 'مهيّأ وموقوف — كلمة المرور فقط',
  'tenantMicrosoft.mode.parkedMeaning':
    'الإعداد محفوظ لكنه موقوف: لا يُقبل الدخول عبر Microsoft، ويدخل الأعضاء بكلمة المرور. إعادة تفعيله لا تحتاج إدخال المعرّفات من جديد.',
  'tenantMicrosoft.status.broken':
    'مفعّل بلا سرّ عميل صالح: كل محاولة دخول عبر Microsoft تنتهي بالفشل حتى يُحفظ سرّ جديد من قسم «السرّ».',
  'tenantMicrosoft.status.directory': 'معرّف الدليل',
  'tenantMicrosoft.status.objectId': 'معرّف كائن التطبيق (Object ID)',
  'tenantMicrosoft.status.client': 'معرّف التطبيق',
  'tenantMicrosoft.status.secret': 'سرّ العميل',
  'tenantMicrosoft.status.directoryType': 'نوع الدليل',
  'tenantMicrosoft.status.domains': 'نطاقات البريد المقبولة',
  'tenantMicrosoft.status.domainsAll': 'كل عنوان يعتمده الدليل',
  'tenantMicrosoft.status.linking': 'الربط بعنوان البريد',
  'tenantMicrosoft.status.registration': 'اسم التسجيل في Entra',
  'tenantMicrosoft.status.updated': 'آخر تعديل',

  /* ---- Sections ------------------------------------------------------ */
  'tenantMicrosoft.sections': 'أقسام الدخول عبر Microsoft',
  'tenantMicrosoft.section.connection': 'الاتصال',
  'tenantMicrosoft.section.secret': 'السرّ',
  'tenantMicrosoft.section.domains': 'النطاقات وأصول العودة',
  'tenantMicrosoft.section.redirect': 'إعادة التوجيه والواجهة',
  'tenantMicrosoft.section.teams': 'مزامنة Teams',
  'tenantMicrosoft.section.invalid': 'فيه حقل يحتاج تصحيحًا',
  'tenantMicrosoft.section.broken': 'مفعّل بلا سرّ صالح',
  'tenantMicrosoft.section.refused': 'فيه حقل رفضه الخادم',

  /* ---- Refusals (`IDP_*`): what to do next; the sentence itself is the API's -------- */
  'tenantMicrosoft.refusal.named': 'رفضه الخادم',
  'tenantMicrosoft.refusal.secretRequired':
    'لم يُحفظ شيء. اكتب سرّ العميل في قسم «سرّ العميل»، أو أوقف الدخول عبر Microsoft لتحفظ الإعداد معلّقًا دون سرّ.',
  'tenantMicrosoft.refusal.directoryTaken':
    'لم يُحفظ شيء. منشأة أخرى تحمل هذا الدليل وحدها. إن كان الدليل مشتركًا فاجعله «مشتركًا» هنا وفي المنشأة الأخرى مع نطاقات لكلٍّ منهما، وإلا فأزِله من المنشأة الأخرى أولًا.',
  'tenantMicrosoft.refusal.domainsRequired':
    'لم يُحفظ شيء. الدليل المشترك يميّز المنشأة بنطاق البريد، فأضف نطاقًا واحدًا على الأقل في قسم «النطاقات».',
  'tenantMicrosoft.refusal.domainClaimed':
    'لم يُحفظ شيء. منشأة أخرى تشارك هذا الدليل تستخدم أحد هذه النطاقات، فلا يُعرف لأيّهما يتبع الداخل منه. أزِل النطاق المشترك من إحداهما.',
  'tenantMicrosoft.refusal.noSignInMethod':
    'لم يُحفظ شيء. فعّل أحد المفتاحين على الأقل: الدخول عبر Microsoft أو الدخول بكلمة المرور.',
  'tenantMicrosoft.refusal.secretStore':
    'لم يُحفظ شيء. هذا إعداد في النشر لا في المنشأة: ينقصه مفتاح تشفير أسرار العملاء. أبلغ فريق المنصّة، أو احفظ تعديلاتك الأخرى دون سرّ جديد.',
  'tenantMicrosoft.refusal.refused':
    'لم يُحفظ شيء، ولم يذكر الخادم سببًا محدّدًا. أعطِ فريق المنصّة معرّف الطلب أدناه.',
  'tenantMicrosoft.refusal.traceId': 'معرّف الطلب',
  'tenantMicrosoft.part.secret': 'ما يثبت به التطبيق هويته لدى Microsoft',
  'tenantMicrosoft.part.domains': 'من يُقبل دخوله',

  /* ---- Register in Entra --------------------------------------------- */
  'tenantMicrosoft.reg.title': 'سجّل هذا العنوان في Microsoft Entra',
  'tenantMicrosoft.reg.description':
    'العنوان الذي تعيد Microsoft المتصفح إليه بعد الدخول. يُسجَّل في تسجيل التطبيق لدى المنشأة — ويُحتاج قبل أن يُحفظ هنا أي شيء.',
  'tenantMicrosoft.reg.uri': 'عنوان إعادة التوجيه (Redirect URI)',
  'tenantMicrosoft.reg.noUri':
    'لا يحمل هذا النشر قالب عنوان إعادة التوجيه (ENTRA_REDIRECT_URI)، فلا عنوان لتسجيله بعد. اطلبه من فريق التشغيل.',
  'tenantMicrosoft.reg.stepsLabel': 'خطوات التسجيل في Entra',
  'tenantMicrosoft.reg.step1':
    'في مركز إدارة Microsoft Entra لدى المنشأة، افتح «App registrations» وأنشئ تسجيلًا جديدًا بحساب من دليلها نفسه، أو افتح تسجيلها الحالي.',
  'tenantMicrosoft.reg.step2':
    'من «Authentication» أضف منصة من نوع «Web» — لا «SPA» ولا «Mobile» — والصق العنوان أعلاه في «Redirect URIs» كما هو حرفًا بحرف. أي اختلاف يُفشل الدخول عند Microsoft بالخطأ AADSTS50011.',
  'tenantMicrosoft.reg.step3':
    'من «Certificates & secrets» أنشئ «Client secret» وانسخ قيمته (Value) لا معرّفه (Secret ID). ثم انسخ من صفحة «Overview» قيمة «Directory (tenant) ID»، ثم قيمة «Application (client) ID»، والصق المعرّفين في قسم «الاتصال» والسرّ في قسم «السرّ».',

  'tenantMicrosoft.returnTo.title': 'إلى أين يعود المتصفح بعد الدخول',
  'tenantMicrosoft.returnTo.scope': 'على مستوى النشر',
  'tenantMicrosoft.returnTo.description':
    'هذه القائمة للنشر كله لا لهذه المنشأة، ولا تُعدَّل من هنا: يغيّرها فريق التشغيل في إعدادات البيئة: ENTRA_DEFAULT_RETURN_TO للعنوان الافتراضي، وENTRA_ALLOWED_RETURN_TO لبقية الأصول. لا يوسّعها رابط واجهة المنشأة، ولا أصول CORS التي تديرها المنشأة بنفسها.',
  'tenantMicrosoft.returnTo.default': 'العنوان الافتراضي',
  'tenantMicrosoft.returnTo.defaultHint': 'يصل إليه المتصفح إن لم يطلب التطبيق وجهة، أو طلب وجهة خارج القائمة.',
  'tenantMicrosoft.returnTo.noDefault': 'غير مضبوط',
  'tenantMicrosoft.returnTo.origins': 'الأصول المسموح بالعودة إليها',
  'tenantMicrosoft.returnTo.originsHint': 'تُقارَن بالأصل كاملًا: البروتوكول والنطاق والمنفذ.',
  'tenantMicrosoft.returnTo.noOrigins': 'لا أصول مسموحة',

  /* ---- The form ------------------------------------------------------ */
  'tenantMicrosoft.form.title': 'إعداد الدخول',
  'tenantMicrosoft.form.oneSave': 'إعداد واحد موزّع على ثلاثة أقسام — الاتصال والسرّ والنطاقات. الحفظ من أيّها يحفظها كلها ويحلّ محلّ المحفوظ.',
  'tenantMicrosoft.form.firstTime':
    'لا إعداد محفوظ لهذه المنشأة بعد. سجّل عنوان إعادة التوجيه (قسم «إعادة التوجيه والواجهة») في Entra أولًا، ثم املأ المعرّفين هنا والسرّ في قسمه.',
  'tenantMicrosoft.form.doors': 'طرق الدخول',
  'tenantMicrosoft.form.enabled': 'قبول الدخول عبر Microsoft',
  'tenantMicrosoft.form.enabledHint': 'إيقافه يُبقي الإعداد محفوظًا ويوقف الدخول به فقط.',
  'tenantMicrosoft.form.enabledLocked':
    'لا يمكن إيقافه والدخول بكلمة المرور مغلق — فعّل كلمة المرور أولًا حتى يبقى للأعضاء طريق للدخول.',
  'tenantMicrosoft.form.password': 'قبول الدخول بالبريد وكلمة المرور',
  'tenantMicrosoft.form.passwordHint': 'إيقافه يجعل الدخول عبر Microsoft هو الطريق الوحيد.',
  'tenantMicrosoft.form.passwordLocked':
    'لا يمكن إغلاقه والدخول عبر Microsoft موقوف — لن يبقى للأعضاء أي طريق للدخول، والخادم يرفض ذلك.',
  'tenantMicrosoft.form.directoryGroup': 'الدليل والتطبيق',
  'tenantMicrosoft.form.mode': 'نوع الدليل',
  'tenantMicrosoft.form.dedicated': 'دليل خاص بالمنشأة',
  'tenantMicrosoft.form.dedicatedHint':
    'للمنشأة دليل Microsoft خاص بها. معرّف الدليل وحده يحدّد المنشأة، ولا تحمله منشأة أخرى.',
  'tenantMicrosoft.form.shared': 'دليل مشترك',
  'tenantMicrosoft.form.sharedHint':
    'دليل واحد يخدم أكثر من منشأة، كمزوّد تقنية مشترك أو دليلنا أثناء التجربة. نطاق البريد هو ما يحدّد المنشأة، فنطاق واحد على الأقل مطلوب.',
  'tenantMicrosoft.form.directoryId': 'معرّف الدليل (Directory / tenant ID)',
  'tenantMicrosoft.form.directoryIdHint': 'من صفحة «Overview» في تسجيل التطبيق.',
  'tenantMicrosoft.form.clientId': 'معرّف التطبيق (Application / client ID)',
  'tenantMicrosoft.form.clientIdHint': 'من الصفحة نفسها. ليس سرًّا.',
  'tenantMicrosoft.form.secret': 'سرّ العميل (Client secret)',
  'tenantMicrosoft.form.secretHint':
    'القيمة (Value) لا معرّف السرّ (Secret ID). تُشفَّر عند الحفظ ولا تُعرض بعده أبدًا؛ اتركه كما هو للإبقاء على المحفوظ.',
  'tenantMicrosoft.form.secretHintNew':
    'القيمة (Value) لا معرّف السرّ (Secret ID). تُشفَّر عند الحفظ ولا تُعرض بعده أبدًا. مطلوب لتفعيل الدخول عبر Microsoft.',
  'tenantMicrosoft.form.secretReplace':
    'يُستخدم السرّ الجديد فور الحفظ. من كان في منتصف الدخول يفشل دخوله ويعيد المحاولة.',
  'tenantMicrosoft.form.linking': 'ربط الأعضاء الحاليين بعنوان بريدهم عند أول دخول',
  'tenantMicrosoft.form.linkingHint':
    'لعضو قائم لم يدخل عبر Microsoft من قبل: يُربط حسابه إن طابق بريده عنوانًا موثّقًا في الدليل، مرة واحدة ثم لا يُستشار البريد. لا تفعّله إلا إن عرفت المنشأة من يملك تعديل عناوين البريد في دليلها، فأي مسؤول مستخدمين هناك يستطيع كتابة أي عنوان.',
  'tenantMicrosoft.form.save': 'حفظ إعداد الدخول',
  'tenantMicrosoft.form.discard': 'تجاهل التغييرات',
  'tenantMicrosoft.form.invalid': 'أكمل الحقول المطلوبة وصحّح المعلَّمة قبل الحفظ.',
  'tenantMicrosoft.form.domainPending': 'في خانة النطاقات نصّ لم يُضف بعد. أضفه أو صحّحه أو امسحه قبل الحفظ.',
  'tenantMicrosoft.form.clean': 'لا تغييرات لحفظها.',
  'tenantMicrosoft.form.saved': 'حُفظ إعداد الدخول عبر Microsoft.',

  /* ---- Domains ------------------------------------------------------- */
  'tenantMicrosoft.domains.label': 'نطاقات البريد المسموح بها',
  'tenantMicrosoft.domains.hintDedicated':
    'فارغة: يُقبل كل عنوان يعتمده الدليل. أضف نطاقًا لتقصر الدخول عليه، فيُستبعد الضيوف وعناوين onmicrosoft.com. المطابقة تامة: النطاق الفرعي يُضاف وحده.',
  'tenantMicrosoft.domains.hintShared': 'مطلوب نطاق واحد على الأقل: به تُعرف منشأة الداخل من الدليل المشترك.',
  'tenantMicrosoft.domains.inputHint': 'اكتب نطاقًا واضغط Enter، أو الصق قائمة مفصولة بفواصل أو أسطر.',
  'tenantMicrosoft.domains.none': 'لا نطاقات بعد.',

  /* ---- Confirm ------------------------------------------------------- */
  'tenantMicrosoft.confirm.title': 'حفظ تغييرات تمسّ دخول الأعضاء؟',
  'tenantMicrosoft.confirm.lead': 'هذا الحفظ:',
  'tenantMicrosoft.confirm.secret':
    'يستبدل سرّ العميل ويستخدمه فورًا. من كان في منتصف الدخول يفشل دخوله، ويفشل دخول الجميع إن لم يطابق السرّ الجديد ما في Entra.',
  'tenantMicrosoft.confirm.directory':
    'يغيّر الدليل. الأعضاء المرتبطون بالدليل الحالي لن يُعرفوا من الدليل الجديد، ومن يدخل منه بلا حساب يصبح طلب انضمام.',
  'tenantMicrosoft.confirm.microsoftOff':
    'يوقف الدخول عبر Microsoft. من لا كلمة مرور له — ومنهم كل من انضم بطلب انضمام — لا يدخل حتى يعيّن كلمة مرور.',
  'tenantMicrosoft.confirm.passwordOff': 'يغلق الدخول بكلمة المرور. من لا حساب له في دليل Microsoft هذا لا يدخل.',
  'tenantMicrosoft.confirm.domainsNarrowed': 'يضيّق نطاقات البريد المقبولة. من عنوانه خارجها يُرفض دخوله عبر Microsoft من الآن.',
  'tenantMicrosoft.confirm.linkingOn':
    'يسمح بربط الحسابات القائمة بعنوان البريد: أي مسؤول مستخدمين في دليل المنشأة يستطيع أن يدخل باسم عضو بكتابة عنوانه.',
  'tenantMicrosoft.confirm.affected': 'يمسّ كل من يدخل إلى {name}.',
  'tenantMicrosoft.confirm.button': 'حفظ على أي حال',

  /* ---- Join requests ------------------------------------------------- */
  'tenantMicrosoft.join.title': 'من يدخل ولا حساب له',
  'tenantMicrosoft.join.known':
    'من يدخل عبر Microsoft من دليل هذه المنشأة — وبنطاق مسموح إن حُدّدت نطاقات — ولا حساب له هنا، يُنشأ له حساب في حالة «طلب انضمام» ولا يدخل. يقرّر المشرف العام للمنشأة قبوله بدور أو رفضه، ويصله بريد بالقرار.',
  'tenantMicrosoft.join.unknown': 'من يدخل من دليل لا تعرفه المنصّة يُبلَّغ أن جهته غير مسجّلة، ولا يُنشأ له شيء.',
  'tenantMicrosoft.join.link': 'طلبات الانضمام لهذه المنشأة',
  'tenantMicrosoft.join.noAccess': 'عرض طلبات الانضمام يحتاج صلاحية «عرض حسابات المنشآت».',

  /* ---- Frontend URL -------------------------------------------------- */
  'tenantMicrosoft.front.title': 'رابط واجهة المنشأة',
  'tenantMicrosoft.front.description':
    'عنوان تطبيق المنشأة نفسها: تُبنى عليه روابط البريد ويُقبل أصله في CORS. يُعدَّل من تبويب «البريد».',
  'tenantMicrosoft.front.current': 'الرابط الحالي',
  'tenantMicrosoft.front.unset': 'غير مضبوط — يُستخدم عنوان النشر',
  'tenantMicrosoft.front.covered': 'أصله ضمن الأصول المسموح بالعودة إليها، فيعود إليه الدخول عبر Microsoft متى طلبه التطبيق.',
  'tenantMicrosoft.front.notCovered':
    'أصله ليس ضمن الأصول المسموح بالعودة إليها: كل دخول عبر Microsoft يبدأ من تطبيق المنشأة سيعود إلى العنوان الافتراضي للنشر لا إليه. يُضاف إلى ENTRA_ALLOWED_RETURN_TO على النشر.',
  'tenantMicrosoft.front.edit': 'تعديله في تبويب البريد',

  /* ---- Leaving ------------------------------------------------------- */
  'tenantMicrosoft.leave.title': 'تجاهل التغييرات غير المحفوظة؟',
  'tenantMicrosoft.leave.consequence':
    'لم تُحفظ تعديلاتك على إعداد الدخول. إن غادرت الآن ضاعت، ويبقى الأعضاء على الإعداد المحفوظ.',
  'tenantMicrosoft.leave.confirm': 'تجاهل ومغادرة',

  /* ---- Errors -------------------------------------------------------- */
  'tenantMicrosoft.error.directoryRequired': 'معرّف الدليل مطلوب.',
  'tenantMicrosoft.error.clientRequired': 'معرّف التطبيق مطلوب.',
  'tenantMicrosoft.error.uuid': 'ليس معرّفًا بصيغة UUID: ثماني خانات ثم أربع وأربع وأربع واثنتا عشرة، تفصلها شرطات.',
  'tenantMicrosoft.error.secretRequired':
    'تفعيل الدخول عبر Microsoft يحتاج سرّ العميل. أدخله، أو أوقف الدخول عبر Microsoft لحفظ الإعداد دونه.',
  'tenantMicrosoft.error.secretTooLong': 'السرّ أطول من 512 محرفًا.',
  'tenantMicrosoft.error.domainTooLong': 'النطاق أطول من 253 محرفًا.',
  'tenantMicrosoft.error.domainIsAddress': 'هذا عنوان بريد لا نطاق. اكتب ما بعد @ وحده، مثل ayed.sa.',
  'tenantMicrosoft.error.domainIsUrl': 'هذا رابط لا نطاق. اكتب اسم النطاق وحده، مثل ayed.sa.',
  'tenantMicrosoft.error.domainShape': 'ليس اسم نطاق صالحًا، مثل ayed.sa.',
  'tenantMicrosoft.error.domainsRequired': 'الدليل المشترك يحتاج نطاقًا واحدًا على الأقل، وإلا لم يدخل منه أحد.',
  'tenantMicrosoft.error.domainsTooMany': 'الحد 50 نطاقًا.',
  'tenantMicrosoft.error.noDoor': 'لا يمكن إغلاق الطريقين معًا: الخادم يرفض إعدادًا لا يترك للأعضاء أي طريق للدخول.',

  /* ---- Teams sync (ATA) ---------------------------------------------- */
  /*
   * The whole point of this panel is to answer «لماذا لا يصل شيء؟» before an
   * operator selects a source rather than after. Sign-in and the Teams sync
   * share an application registration and share no scope at all, so a
   * directory that signs members in perfectly can be unable to read one
   * message — and that sentence is what these strings have to carry.
   */
  'tenantMicrosoft.teams.title': 'ما تستطيع مزامنة Teams فعله',
  'tenantMicrosoft.teams.lead':
    'هذه الموافقات منفصلة تمامًا عن الدخول عبر Microsoft: التسجيل نفسه، ولا يشتركان في صلاحية واحدة. فقد يدخل الأعضاء بلا مشكلة ولا تستطيع المزامنة قراءة رسالة واحدة.',
  'tenantMicrosoft.teams.recheck': 'إعادة الفحص',
  'tenantMicrosoft.teams.checkedAt': 'فُحص',
  'tenantMicrosoft.teams.granted': 'ممنوحة',
  'tenantMicrosoft.teams.missing': 'ناقصة',
  'tenantMicrosoft.teams.needs': 'تحتاج',
  'tenantMicrosoft.teams.rolesTitle': 'الصلاحيات الممنوحة على هذا التسجيل',
  'tenantMicrosoft.teams.rolesEmpty': 'لا توجد أي صلاحية تطبيق ممنوحة على هذا التسجيل.',
  'tenantMicrosoft.teams.rolesHint':
    'كما يسمّيها Microsoft. حين تختلف النصيحة أعلاه عن بوّابة Azure، هذه هي القائمة التي تحسم.',
  'tenantMicrosoft.teams.unreadable': 'تعذّر سؤال Microsoft',
  'tenantMicrosoft.teams.unreadableBody':
    'لم يُفحص شيء: لم يتمكّن النظام من الحصول على رمز بهذه الاعتمادات. صحّح الاتصال أو السرّ أولًا، فالإجابة أدناه ستبقى فارغة حتى ذلك.',

  /* Each capability named as the operator experiences its absence. */
  'tenantMicrosoft.teams.cap.DISCOVER_CHANNELS': 'سرد الفرق والقنوات',
  'tenantMicrosoft.teams.cap.DISCOVER_CHANNELS.why':
    'بدونها يبقى منتقي المصادر فارغًا، فلا يمكن اختيار شيء ولا مزامنة شيء.',
  'tenantMicrosoft.teams.cap.READ_CHANNEL_MESSAGES': 'قراءة رسائل القناة وردودها',
  'tenantMicrosoft.teams.cap.READ_CHANNEL_MESSAGES.why':
    'بدونها تُختار القناة وتبقى فارغة — وهذا أكثر ما يُقرأ خطأً على أنه عطل.',
  'tenantMicrosoft.teams.cap.READ_CHANNEL_FILES': 'ملفات القناة على SharePoint',
  'tenantMicrosoft.teams.cap.READ_CHANNEL_FILES.why':
    'بدونها تصل الرسائل ويُسجَّل كل مرفق وكل ملف «متخطّى» مع السبب، لا أن يُهمل بصمت.',
  'tenantMicrosoft.teams.cap.DISCOVER_GROUP_CHATS': 'محادثات المجموعات',
  'tenantMicrosoft.teams.cap.DISCOVER_GROUP_CHATS.why':
    'معطّلة عن قصد. تحتاج Chat.Read.All التي تقرأ كل محادثة في الدليل بما فيها الخاصّة، وهي مسجّلة عندنا كصلاحية سترفضها إدارة أيد.',

  /* ---- The allowlist ------------------------------------------------- */
  'tenantMicrosoft.teams.sourcesTitle': 'المصادر المختارة',
  'tenantMicrosoft.teams.sourcesEmpty': 'لم تُختر أي قناة.',
  'tenantMicrosoft.teams.sourcesEmptyBody':
    'لا شيء يُستورد تلقائيًّا. الاكتشاف قد يرى كل قناة تسمح بها الصلاحية، ولا يُسحب إلا ما يُختار هنا صراحةً.',
  'tenantMicrosoft.teams.sourcesBlocked':
    'اختيار المصادر غير متاح حتى تُمنح «سرد الفرق والقنوات»، لأن المنتقي يُملأ منها.',

  /* ---- Honesty about what has not been tested ------------------------ */
  'tenantMicrosoft.teams.unverified': 'لم تُشغَّل مقابل مصدر Microsoft حقيقي',
  'tenantMicrosoft.teams.unverifiedBody':
    'بُني الاستيراد والمزامنة المستمرة على هذه الصلاحيات ولم يُجرَّبا فعليًّا، لأن أيًّا منها غير ممنوح بعد. التفاصيل في docs/security/teams-sync-scopes.md.',

  /* ---- The channels themselves (ATA) --------------------------------- */
  'tenantMicrosoft.teams.channelsTitle': 'القنوات المتاحة',
  'tenantMicrosoft.teams.channelsLead':
    'كل قناة يسمح تسجيل Microsoft برؤيتها. ظهورها هنا لا يعني أنها تُقرأ — لا يُسحب شيء من قناة حتى تُختار.',
  'tenantMicrosoft.teams.reload': 'إعادة الفحص',
  'tenantMicrosoft.teams.col.team': 'الفريق',
  'tenantMicrosoft.teams.col.channel': 'القناة',
  'tenantMicrosoft.teams.col.type': 'النوع',
  'tenantMicrosoft.teams.col.state': 'الحالة',
  'tenantMicrosoft.teams.type.standard': 'عامّة',
  'tenantMicrosoft.teams.type.private': 'خاصّة',
  'tenantMicrosoft.teams.type.shared': 'مشتركة',
  'tenantMicrosoft.teams.chosen': 'مختارة',
  'tenantMicrosoft.teams.notChosen': 'غير مختارة',
  'tenantMicrosoft.teams.noChannels': 'لم تُعثر على أي قناة',
  'tenantMicrosoft.teams.noChannelsBody':
    'التسجيل لا يرى أي فريق. تحقّق من منح «سرد الفرق والقنوات» أعلاه، فالاكتشاف يُملأ منها.',
  'tenantMicrosoft.teams.needsDiscovery':
    'سرد القنوات يحتاج صلاحية «سرد الفرق والقنوات»، وهي غير ممنوحة بعد — فالجدول يبقى فارغًا حتى تُمنح.',
  /* Distinct from `teams.unreadable` above, which is «Microsoft could not be
     asked at all». This one is «it answered, and these teams it would not
     open» — a different problem with a different remedy. */
  'tenantMicrosoft.teams.closedTeamsTitle': 'فرق تعذّر فتحها',
  'tenantMicrosoft.teams.closedTeamsBody':
    'رآها التسجيل ولم يستطع سرد قنواتها. هذه حقيقة صلاحيات يستطيع المسؤول معالجتها، ولولا ذكرها لقُرئت كفرق بلا قنوات.',
  'tenantMicrosoft.teams.counts': 'رسائل · ملفات',
  'tenantMicrosoft.teams.notImported':
    'لم يُستورد شيء بعد: الاستيراد نفسه غير مبنيّ، فالقناة تُختار وتُحفظ ولا يُقرأ منها شيء حتى الآن.',

  /* ---- Files per channel, and group chats (ATA) ---------------------- */
  'tenantMicrosoft.teams.col.files': 'الملفات',
  'tenantMicrosoft.teams.noFolder': 'لا مجلّد',
  'tenantMicrosoft.teams.filesAtLeast': 'على الأقل',

  'tenantMicrosoft.teams.chatsTitle': 'محادثات المجموعات',
  'tenantMicrosoft.teams.chatsLead':
    'المحادثات التي فيها أكثر من شخص. لا يسمح Microsoft بسردها على مستوى الدليل لتطبيق، فتُجمع من كل عضو ربط هويّته — ومحادثة لا يشارك فيها أحد منهم لا تظهر هنا.',
  'tenantMicrosoft.teams.chatsCoverage': 'سُئل {asked} من {total} عضوًا نشطًا',
  'tenantMicrosoft.teams.chatsCoverageWhy':
    'الأعضاء الذين سجّلوا الدخول عبر Microsoft وحدهم لهم هويّة تُسأل عنها. الفرق بين الرقمين هو الفرق بين «لا توجد محادثات» و«لم نستطع سؤال الجميع».',
  'tenantMicrosoft.teams.col.chat': 'المحادثة',
  'tenantMicrosoft.teams.col.chatType': 'النوع',
  'tenantMicrosoft.teams.col.members': 'أعضاء معروفون',
  'tenantMicrosoft.teams.col.activity': 'آخر نشاط',
  'tenantMicrosoft.teams.chat.group': 'مجموعة',
  'tenantMicrosoft.teams.chat.meeting': 'اجتماع',
  'tenantMicrosoft.teams.chatUntitled': 'بلا عنوان',
  'tenantMicrosoft.teams.noChats': 'لم تُعثر على محادثات مجموعات',
  'tenantMicrosoft.teams.noChatsBody':
    'لا عضو ربط هويّة Microsoft بعد، أو لا أحد منهم في محادثة جماعية. هذا ليس بالضرورة أن المنشأة بلا محادثات.',
  'tenantMicrosoft.teams.chatsNotSelectable':
    'محادثات المجموعات تُعرض ولا تُختار بعد — الاستيراد منها غير مبنيّ.',
} as const satisfies Messages<'tenantMicrosoft'>;
